import type { InspectionData, InspectorSegment } from "../types.ts";
import { openPhotoLightbox } from "./PhotoLightbox.ts";

export class InspectorPanel {
  private el: HTMLElement;
  private videoEl: HTMLVideoElement;
  private videoSelect: HTMLSelectElement;
  private photoImg: HTMLImageElement;
  private photoNav: HTMLElement;
  private listEl: HTMLElement;
  private emptyEl: HTMLElement;

  private segments: InspectorSegment[] = [];
  private activeIdx = -1;
  private photoIdx = 0;
  private mediaUrlFn: (filename: string) => string = () => "";

  constructor(pane: HTMLElement) {
    this.el = pane;
    this.el.innerHTML = `
      <div class="ip-video-wrap">
        <select class="ip-video-select"></select>
        <video class="ip-video" controls preload="metadata"></video>
        <div class="ip-no-video">No video available</div>
      </div>
      <div class="ip-photo-wrap">
        <img class="ip-photo" alt="" />
        <div class="ip-photo-nav">
          <button class="ip-photo-prev">&lsaquo;</button>
          <span class="ip-photo-counter"></span>
          <button class="ip-photo-next">&rsaquo;</button>
        </div>
        <div class="ip-no-photo">No photo for this segment</div>
      </div>
      <div class="ip-segments">
        <div class="ip-seg-header">Segments</div>
        <div class="ip-seg-list"></div>
        <div class="ip-empty">No inspection data available.</div>
      </div>
    `;

    this.videoEl = this.el.querySelector(".ip-video")!;
    this.videoSelect = this.el.querySelector(".ip-video-select")!;
    this.photoImg = this.el.querySelector(".ip-photo")!;
    this.photoNav = this.el.querySelector(".ip-photo-nav")!;
    this.listEl = this.el.querySelector(".ip-seg-list")!;
    this.emptyEl = this.el.querySelector(".ip-empty")!;

    // Video time tracking — highlight active segment
    this.videoEl.addEventListener("timeupdate", () => this._onTimeUpdate());

    // Video source selector
    this.videoSelect.addEventListener("change", () => {
      this.videoEl.src = this.mediaUrlFn(this.videoSelect.value);
      this.videoEl.load();
    });

    // Photo navigation
    this.el.querySelector(".ip-photo-prev")!.addEventListener("click", () => this._navPhoto(-1));
    this.el.querySelector(".ip-photo-next")!.addEventListener("click", () => this._navPhoto(1));

    // Click photo → open fullscreen lightbox with full-resolution image
    this.photoImg.style.cursor = "zoom-in";
    this.photoImg.addEventListener("click", () => this._openLightbox());
  }

  /** Pause video (called by SidePanel when switching away from this tab). */
  pause(): void {
    this.videoEl.pause();
  }

  load(data: InspectionData, mediaUrl: (filename: string) => string): void {
    this.mediaUrlFn = mediaUrl;
    this.segments = data.segments;
    this.activeIdx = -1;
    this.photoIdx = 0;

    // Videos
    const noVideo = this.el.querySelector(".ip-no-video") as HTMLElement;
    if (data.videos.length > 0) {
      this.videoEl.style.display = "";
      this.videoSelect.style.display = data.videos.length > 1 ? "" : "none";
      noVideo.style.display = "none";

      this.videoSelect.innerHTML = "";
      for (const v of data.videos) {
        const opt = document.createElement("option");
        opt.value = v;
        opt.textContent = v;
        this.videoSelect.appendChild(opt);
      }
      this.videoEl.src = mediaUrl(data.videos[0]);
      this.videoEl.load();
    } else {
      this.videoEl.style.display = "none";
      this.videoSelect.style.display = "none";
      noVideo.style.display = "";
    }

    // Segments list
    this._renderSegments();

    // Auto-select first segment
    if (this.segments.length > 0) {
      this._selectSegment(0, false);
    } else {
      this._showPhoto(null);
    }
  }

  private _renderSegments(): void {
    this.listEl.innerHTML = "";
    this.emptyEl.style.display = this.segments.length ? "none" : "block";

    for (let i = 0; i < this.segments.length; i++) {
      const seg = this.segments[i];
      const item = document.createElement("div");
      item.className = "ip-seg-item";
      item.dataset.idx = String(i);
      item.innerHTML = `
        <span class="ip-seg-time">${_fmtTime(seg.time_start)} – ${_fmtTime(seg.time_end)}</span>
        <span class="ip-seg-text">${_escHtml(seg.text)}</span>
        ${seg.photos.length ? `<span class="ip-seg-photos">${seg.photos.length} photo${seg.photos.length > 1 ? "s" : ""}</span>` : ""}
      `;
      item.addEventListener("click", () => this._selectSegment(i, true));
      this.listEl.appendChild(item);
    }
  }

  private _selectSegment(idx: number, seekVideo: boolean): void {
    if (idx === this.activeIdx && !seekVideo) return;

    const prev = this.listEl.querySelector(".ip-seg-item.active");
    if (prev) prev.classList.remove("active");

    this.activeIdx = idx;
    this.photoIdx = 0;
    const seg = this.segments[idx];

    const item = this.listEl.children[idx] as HTMLElement;
    item.classList.add("active");
    item.scrollIntoView({ block: "nearest", behavior: "smooth" });

    if (seekVideo && this.videoEl.src) {
      this.videoEl.currentTime = seg.time_start;
    }

    if (seg.photos.length > 0) {
      this._showPhoto(seg.photos[0]);
      this._updatePhotoNav(seg);
    } else {
      this._showPhoto(null);
    }
  }

  private _showPhoto(filename: string | null): void {
    const noPhoto = this.el.querySelector(".ip-no-photo") as HTMLElement;
    if (filename) {
      this.photoImg.src = this.mediaUrlFn(filename) + "?size=thumb";
      this.photoImg.style.display = "";
      noPhoto.style.display = "none";
    } else {
      this.photoImg.style.display = "none";
      this.photoImg.src = "";
      noPhoto.style.display = "";
      this.photoNav.style.display = "none";
    }
  }

  private _openLightbox(): void {
    if (this.activeIdx < 0) return;
    const seg = this.segments[this.activeIdx];
    if (!seg || seg.photos.length === 0) return;
    openPhotoLightbox({
      photos: seg.photos,
      startIdx: this.photoIdx,
      urlFor: (name) => this.mediaUrlFn(name),
    });
  }

  private _updatePhotoNav(seg: InspectorSegment): void {
    if (seg.photos.length <= 1) {
      this.photoNav.style.display = "none";
      return;
    }
    this.photoNav.style.display = "";
    const counter = this.photoNav.querySelector(".ip-photo-counter")!;
    counter.textContent = `${this.photoIdx + 1} / ${seg.photos.length}`;
  }

  private _navPhoto(dir: number): void {
    if (this.activeIdx < 0) return;
    const seg = this.segments[this.activeIdx];
    if (seg.photos.length <= 1) return;

    this.photoIdx = (this.photoIdx + dir + seg.photos.length) % seg.photos.length;
    this._showPhoto(seg.photos[this.photoIdx]);
    this._updatePhotoNav(seg);
  }

  private _onTimeUpdate(): void {
    if (this.segments.length === 0) return;
    const t = this.videoEl.currentTime;

    for (let i = 0; i < this.segments.length; i++) {
      const seg = this.segments[i];
      if (t >= seg.time_start && t < seg.time_end) {
        if (i !== this.activeIdx) {
          this._selectSegment(i, false);
        }
        return;
      }
    }
  }

  dispose(): void {
    this.videoEl.pause();
    this.videoEl.src = "";
    this.el.innerHTML = "";
  }
}

function _fmtTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function _escHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
